import { provideZoneChangeDetection, isDevMode, ErrorHandler } from "@angular/core";
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { provideServiceWorker } from '@angular/service-worker';

// Sentry initialization - MUST be before Angular bootstrap
import { initializeSentry, SentryErrorHandler } from './app/core/services/sentry.service';
import { isProductionLike } from './app/core/config/environment-utils';
initializeSentry();

/**
 * Razor Trading Platform - Admin Interface
 * 
 * High-frequency trading administration platform with multi-tenant architecture,
 * real-time market data, and comprehensive risk management.
 * 
 * Features:
 * - Multi-tenant isolation with automatic tenant scoping
 * - Real-time data streaming and visualization
 * - Advanced risk management and compliance
 * - Machine learning anomaly detection
 * - Symbol mapping and data normalization
 * - Regulatory reporting and audit trails
 */

/**
 * Sanitize error messages to prevent XSS attacks in bootstrap error display
 * SECURITY: Critical defense against XSS through malicious error messages
 */
function sanitizeErrorMessage(input: string): string {
  return input.replace(/[<>'"&]/g, (char) => {
    const entities: Record<string, string> = {
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
      '&': '&amp;'
    };
    return entities[char] || char;
  });
}

if (!isProductionLike()) {
  // eslint-disable-next-line no-console
  console.log('Starting Razor Trading Platform Admin Interface...');
}

// Service Worker registration for offline support (PWA)
const serviceWorkerProvider = provideServiceWorker('ngsw-worker.js', {
  enabled: !isDevMode(),
  registrationStrategy: 'registerWhenStable:30000'
});

// Bootstrap the application with standalone components and runtime configuration
bootstrapApplication(AppComponent, {
  ...appConfig,
  providers: [
    provideZoneChangeDetection(),
    serviceWorkerProvider,
    // Sentry error handler for unhandled exceptions
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    ...appConfig.providers
  ]
})
  .then(() => {
    if (!isProductionLike()) {
      // eslint-disable-next-line no-console
      console.log('Application started successfully');
    }
  })
  .catch(err => {
    if (!isProductionLike()) {
      // eslint-disable-next-line no-console
      console.error('Error starting the application:', err);
    }

    // SECURITY: Sanitize error message before displaying to prevent XSS
    const sanitizedMessage = sanitizeErrorMessage(err?.message || 'Unknown error occurred');

    // Show user-friendly error message
    // SECURITY: Use textContent instead of innerHTML for dynamic content
    const errorContainer = document.createElement('div');
    errorContainer.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #fff;
      border: 2px solid #dc2626;
      border-radius: 8px;
      padding: 24px;
      box-shadow: 0 10px 25px rgba(0,0,0,0.2);
      font-family: 'Geist Sans', 'Space Grotesk', system-ui, sans-serif;
      max-width: 400px;
      text-align: center;
      z-index: var(--z-notification);
    `;

    const heading = document.createElement('h2');
    heading.style.cssText = 'margin: 0 0 16px 0; color: #dc2626;';
    heading.textContent = '🚨 Application Failed to Start';

    const message = document.createElement('p');
    message.style.cssText = 'margin: 0 0 16px 0; color: #374151;';
    message.textContent = 'The Razor Trading Platform could not initialize. Please check your network connection and try refreshing the page.';

    const refreshButton = document.createElement('button');
    refreshButton.style.cssText = `
      background: #dc2626;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 8px 16px;
      cursor: pointer;
      font-weight: 500;
    `;
    refreshButton.textContent = 'Refresh Page';
    refreshButton.onclick = () => window.location.reload();

    const details = document.createElement('details');
    details.style.cssText = 'margin-top: 16px; text-align: left;';

    const summary = document.createElement('summary');
    summary.style.cssText = 'cursor: pointer; font-weight: 500;';
    summary.textContent = 'Technical Details';

    const pre = document.createElement('pre');
    pre.style.cssText = 'background: #f3f4f6; padding: 8px; border-radius: 4px; margin-top: 8px; font-size: 12px; overflow: auto;';
    // SECURITY: Use textContent to prevent XSS from error messages
    pre.textContent = sanitizedMessage;

    details.appendChild(summary);
    details.appendChild(pre);

    errorContainer.appendChild(heading);
    errorContainer.appendChild(message);
    errorContainer.appendChild(refreshButton);
    errorContainer.appendChild(details);

    document.body.appendChild(errorContainer);
  });
